import { getDb } from '../db';
import type { PersonalBigram, PersonalUnigram } from '../../core/types';

/**
 * The person's own word and word-pair counts. Phase 2's `LearningService` writes
 * them and its prediction scoring weights them above the corpus — within days,
 * ranking is dominated by how *this person* talks rather than by a news corpus.
 *
 * Phase 1 only needs read and bulk-write, so export/import can carry them. They are
 * in the export for a blunt reason: they are the part of this database that cannot
 * be recreated from anything else.
 */
export const personalNgramRepo = {
  /**
   * Count one spoken utterance's worth of words and pairs, in a single transaction.
   *
   * Read-modify-write per word rather than a bulk overwrite: two utterances could otherwise race and
   * the later one would clobber the earlier one's increments. One transaction covers both stores so a
   * crash between them cannot leave a unigram counted and its bigram lost.
   */
  async record(words: readonly string[], spokenAt: string): Promise<void> {
    if (words.length === 0) return;
    const db = await getDb();
    const tx = db.transaction(['personalUnigrams', 'personalBigrams'], 'readwrite');
    const unigrams = tx.objectStore('personalUnigrams');
    const bigrams = tx.objectStore('personalBigrams');

    for (const [index, word] of words.entries()) {
      const existing = await unigrams.get(word);
      await unigrams.put({
        word,
        count: (existing?.count ?? 0) + 1,
        lastUsedAt: spokenAt,
      });

      const next = words[index + 1];
      if (next === undefined) continue;
      const pair = await bigrams.get([word, next]);
      await bigrams.put({
        prev: word,
        next,
        count: (pair?.count ?? 0) + 1,
        lastUsedAt: spokenAt,
      });
    }

    await tx.done;
  },

  /** How many distinct words this person has been heard to say. Shown in care mode. */
  async wordCount(): Promise<number> {
    const db = await getDb();
    return db.count('personalUnigrams');
  },

  async allUnigrams(): Promise<PersonalUnigram[]> {
    const db = await getDb();
    return db.getAll('personalUnigrams');
  },

  async allBigrams(): Promise<PersonalBigram[]> {
    const db = await getDb();
    return db.getAll('personalBigrams');
  },

  async bulkPutUnigrams(rows: readonly PersonalUnigram[]): Promise<void> {
    const db = await getDb();
    const tx = db.transaction('personalUnigrams', 'readwrite');
    await Promise.all(rows.map((row) => tx.store.put(row)));
    await tx.done;
  },

  async bulkPutBigrams(rows: readonly PersonalBigram[]): Promise<void> {
    const db = await getDb();
    const tx = db.transaction('personalBigrams', 'readwrite');
    await Promise.all(rows.map((row) => tx.store.put(row)));
    await tx.done;
  },
};
